import { History } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import type { HelmCommunication, WorkflowEvent } from '@/lib/api/types'
import { communicationActivity, workflowEventActivity } from '@/lib/record-activity'
import { personName, useHelmPeople } from '@/src/hooks/useHelmPeople'
import { Translatable } from '@/src/components/translation/Translatable'

function formatDateTime(value: string) {
  return new Date(value).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Africa/Kampala',
  })
}

/**
 * A consignment's timeline: its workflow events (phase, state, tasks, owner
 * and due date) and its messages to the customer, newest first, as the
 * inquiry page's activity log shows an inquiry's.
 */
export function RecordActivity({
  createdAt,
  events,
  communications,
}: {
  createdAt: string
  events: WorkflowEvent[]
  communications: HelmCommunication[]
}) {
  const people = useHelmPeople()
  const nameOf = (userId: string | null | undefined) =>
    !userId
      ? null
      : userId === people.data?.meId
        ? 'You'
        : personName(people.data?.users.get(userId))
  const activities = [
    {
      id: 'created',
      title: 'Consignment created',
      description: '',
      timestamp: createdAt,
      label: 'System',
    },
    ...events.map((event) => ({
      id: `workflow-${event.id}`,
      ...workflowEventActivity(event, nameOf),
      timestamp: event.occurred_at,
      label: 'Workflow',
    })),
    ...communications.map((communication) => ({
      id: `communication-${communication.id}`,
      ...communicationActivity(communication),
    })),
  ].sort(
    (first, second) =>
      new Date(second.timestamp ?? 0).getTime() - new Date(first.timestamp ?? 0).getTime(),
  )

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle>Activity</CardTitle>
            <CardDescription>
              Workflow, owner, due date and messages to the customer, newest first.
            </CardDescription>
          </div>
          <Badge variant="outline">{activities.length}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <ol className="divide-y rounded-md border" aria-label="Consignment activity">
          {activities.map((activity) => (
            <li key={activity.id} className="flex gap-3 p-4">
              <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border bg-background text-muted-foreground">
                <History className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="font-medium">{activity.title}</div>
                  {activity.label && <Badge variant="outline">{activity.label}</Badge>}
                </div>
                {activity.timestamp && (
                  <div className="text-xs text-muted-foreground">
                    {formatDateTime(activity.timestamp)}
                  </div>
                )}
                <Translatable
                  text={activity.description}
                  context="activity"
                  className="whitespace-pre-wrap text-sm text-muted-foreground"
                  label={activity.title}
                />
              </div>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  )
}
